import { useCallback, useRef, useState } from "react"
import { IngestDataType, MAX_COMMENT_IMAGES } from "@nova/schemas"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { createIdFromFile } from "@nova/util-strings"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { setGlobalNotificationContext } from "user/profile/slice"

const MAX_IMAGE_BYTES = 10 * 1024 * 1024

export type CommentImage = {
    id: string
    previewUrl: string
    status: "uploading" | "done" | "error"
}

export const useCommentImageUpload = (entityId: string) => {
    const dispatch = useAppDispatch()
    const orgId = useAppSelector(selectActiveWorkspaceId()) ?? ""
    const projectId = useAppSelector(selectActivatedFireId) ?? ""
    const [images, setImages] = useState<CommentImage[]>([])
    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const countRef = useRef(0)

    const patch = (id: string, next: Partial<CommentImage>) =>
        setImages((current) =>
            current.map((image) => (image.id === id ? { ...image, ...next } : image))
        )

    const notifyUploadFailed = useCallback(
        (message: string) =>
            dispatch(
                setGlobalNotificationContext({
                    message,
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 3000,
                })
            ),
        [dispatch]
    )

    const upload = useCallback(
        async (file: File) => {
            if (!file.type.startsWith("image/")) {
                notifyUploadFailed(`${file.name} is not an image.`)
                return
            }
            if (file.size > MAX_IMAGE_BYTES) {
                notifyUploadFailed(
                    `${file.name} is larger than ${MAX_IMAGE_BYTES / 1024 / 1024}MB.`
                )
                return
            }
            if (countRef.current >= MAX_COMMENT_IMAGES) {
                notifyUploadFailed(`You can attach at most ${MAX_COMMENT_IMAGES} images.`)
                return
            }
            countRef.current += 1

            const id = createIdFromFile(file).replace(/[^A-Za-z0-9._-]/g, "-")
            const previewUrl = URL.createObjectURL(file)

            setImages((current) => [...current, { id, previewUrl, status: "uploading" }])

            try {
                await fileManagementApi.putFile(file, {
                    filename: id,
                    tags: {
                        dataType: IngestDataType.CommentImage,
                        orgId,
                        projectId,
                        entityId,
                    },
                    onIngestError: () => {
                        patch(id, { status: "error" })
                        notifyUploadFailed(`Failed to upload ${file.name}, please try again.`)
                    },
                })
                patch(id, { status: "done" })
            } catch {
                patch(id, { status: "error" })
                notifyUploadFailed("Failed to upload image, please try again.")
            }
        },
        [orgId, projectId, entityId, notifyUploadFailed]
    )

    const toggleSelect = (id: string) =>
        setSelectedIds((current) =>
            current.includes(id) ? current.filter((selected) => selected !== id) : [...current, id]
        )

    const removeSelected = () => {
        setImages((current) => {
            const kept = current.filter((image) => !selectedIds.includes(image.id))
            current
                .filter((image) => selectedIds.includes(image.id))
                .forEach((image) => URL.revokeObjectURL(image.previewUrl))
            countRef.current = kept.length
            return kept
        })
        setSelectedIds([])
    }

    const reset = () => {
        setImages((current) => {
            current.forEach((image) => URL.revokeObjectURL(image.previewUrl))
            return []
        })
        setSelectedIds([])
        countRef.current = 0
    }

    const uploadedFileIds = images
        .filter((image) => image.status === "done")
        .map((image) => image.id)
    const isUploading = images.some((image) => image.status === "uploading")
    const isFull = images.length >= MAX_COMMENT_IMAGES

    return {
        images,
        selectedIds,
        upload,
        toggleSelect,
        removeSelected,
        reset,
        uploadedFileIds,
        isUploading,
        isFull,
    }
}
